import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header.tsx';
import useTheme from '../hooks/useTheme.ts';
import { WorkflowProvider, useWorkflow, WORKFLOW_STEPS } from './workflow/WorkflowContext.tsx';
import Pads from './workflow/Pads.tsx';
import WellConfig from './workflow/WellConfig.tsx';
import FracSchedule from './workflow/FracSchedule.tsx';
import PadDeploy from './workflow/PadDeploy.tsx';

// Machina Remote Seismos App - Interactive Prototype

const MachinaPrototypeContent = ({ currentScreen, setCurrentScreen }) => {
  const navigate = useNavigate();
  const {
    state: workflowState,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    resetWorkflow,
    validateStep,
  } = useWorkflow();
  const { currentStep } = workflowState;
  const [selectedWell, setSelectedWell] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { theme } = useTheme();
  
  // Dashboard state
  const [expandedJob, setExpandedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('all');

  // Jobs data for Operations Dashboard
  const jobsData = [
    {
      id: 1,
      jobName: 'Rio San Jacinto 38-43 Pad 1',
      operator: 'SM Energy',
      fleet: 'Fleet 1 (1000|18)',
      district: 'Midland',
      status: 'Active',
      fracEfficiency: 99.5,
      totalPumpTime: '575 hrs, 17 mins',
      totalNPT: '14 hrs, 55 mins',
      avgDailyStageCount: 17.3,
      projectedEndDate: '08/05/2024',
      dailyPumpingHours: 20.6,
      avgTransitionTime: '12 mins',
      completedStages: 414,
      totalStages: 414,
      wells: [
        { name: 'RSJ 38-43 UNIT 1 #111', completed: 69, proposed: 69 },
        { name: 'RSJ 38-43 UNIT 1 #112', completed: 69, proposed: 69 },
        { name: 'RSJ 38-43 UNIT 1 #113', completed: 69, proposed: 69 },
        { name: 'RSJ 38-43 UNIT 1 #121', completed: 69, proposed: 69 },
        { name: 'RSJ 38-43 UNIT 1 #123', completed: 69, proposed: 69 },
        { name: 'RSJ 38-43 UNIT 1 #224', completed: 69, proposed: 69 },
      ],
    },
    {
      id: 2,
      jobName: 'Blackstone Pad F',
      operator: 'ConocoPhillips',
      fleet: 'Fleet 2 (2100|37)',
      district: 'Northeast',
      status: 'Active',
      fracEfficiency: 97.2,
      totalPumpTime: '312 hrs, 45 mins',
      totalNPT: '9 hrs, 12 mins',
      avgDailyStageCount: 15.8,
      projectedEndDate: '08/12/2024',
      dailyPumpingHours: 19.2,
      avgTransitionTime: '14 mins',
      completedStages: 89,
      totalStages: 155,
      wells: [
        { name: 'Blackstone F #1H', completed: 31, proposed: 31 },
        { name: 'Blackstone F #2H', completed: 31, proposed: 31 },
        { name: 'Blackstone F #3H', completed: 27, proposed: 31 },
        { name: 'Blackstone F #4H', completed: 0, proposed: 31 },
        { name: 'Blackstone F #5H', completed: 0, proposed: 31 },
      ],
    },
    {
      id: 3,
      jobName: 'Eagle Point Pad C',
      operator: 'Devon Energy',
      fleet: 'Fleet 3 (4000|09)',
      district: 'Pleasanton',
      status: 'Active',
      fracEfficiency: 98.1,
      totalPumpTime: '428 hrs, 22 mins',
      totalNPT: '8 hrs, 45 mins',
      avgDailyStageCount: 16.4,
      projectedEndDate: '08/08/2024',
      dailyPumpingHours: 20.1,
      avgTransitionTime: '11 mins',
      completedStages: 112,
      totalStages: 150,
      wells: [
        { name: 'Eagle Point C #1H', completed: 30, proposed: 30 },
        { name: 'Eagle Point C #2H', completed: 30, proposed: 30 },
        { name: 'Eagle Point C #3H', completed: 30, proposed: 30 },
        { name: 'Eagle Point C #4H', completed: 22, proposed: 30 },
        { name: 'Eagle Point C #5H', completed: 0, proposed: 30 },
      ],
    },
    {
      id: 4,
      jobName: 'Mesa Verde BS Unit',
      operator: 'EOG Resources',
      fleet: 'Fleet 4 (5000|04)',
      district: 'Vernal',
      status: 'Completed',
      fracEfficiency: 99.1,
      totalPumpTime: '682 hrs, 10 mins',
      totalNPT: '6 hrs, 22 mins',
      avgDailyStageCount: 18.2,
      projectedEndDate: '07/28/2024',
      dailyPumpingHours: 21.4,
      avgTransitionTime: '10 mins',
      completedStages: 180,
      totalStages: 180,
      wells: [
        { name: 'Mesa Verde #1H', completed: 30, proposed: 30 },
        { name: 'Mesa Verde #2H', completed: 30, proposed: 30 },
        { name: 'Mesa Verde #3H', completed: 30, proposed: 30 },
        { name: 'Mesa Verde #4H', completed: 30, proposed: 30 },
        { name: 'Mesa Verde #5H', completed: 30, proposed: 30 },
        { name: 'Mesa Verde #6H', completed: 30, proposed: 30 },
      ],
    },
    {
      id: 5,
      jobName: 'North Field Pad A',
      operator: 'Apache Corporation',
      fleet: 'Fleet 5 (5000|08)',
      district: 'Longview',
      status: 'Scheduled',
      fracEfficiency: null,
      totalPumpTime: '--',
      totalNPT: '--',
      avgDailyStageCount: null,
      projectedEndDate: '09/15/2024',
      dailyPumpingHours: null,
      avgTransitionTime: '--',
      completedStages: 0,
      totalStages: 60,
      wells: [
        { name: 'North Field A #1H', completed: 0, proposed: 30 },
        { name: 'North Field A #2H', completed: 0, proposed: 30 },
      ],
    },
    {
      id: 6,
      jobName: 'Permian Basin Unit 7',
      operator: 'Magnolia Oil',
      fleet: 'Fleet 1 (1000|20)',
      district: 'Midland',
      status: 'Completed',
      fracEfficiency: 98.8,
      totalPumpTime: '445 hrs, 33 mins',
      totalNPT: '5 hrs, 18 mins',
      avgDailyStageCount: 17.1,
      projectedEndDate: '07/15/2024',
      dailyPumpingHours: 20.8,
      avgTransitionTime: '11 mins',
      completedStages: 120,
      totalStages: 120,
      wells: [
        { name: 'PB Unit 7 #1H', completed: 40, proposed: 40 },
        { name: 'PB Unit 7 #2H', completed: 40, proposed: 40 },
        { name: 'PB Unit 7 #3H', completed: 40, proposed: 40 },
      ],
    },
    {
      id: 7,
      jobName: 'WTH 20-8 Pad 4',
      operator: 'Endeavor Energy Resources',
      fleet: 'Fleet 2 (2100|38)',
      district: 'Midland',
      status: 'Scheduled',
      fracEfficiency: null,
      totalPumpTime: '--',
      totalNPT: '--',
      avgDailyStageCount: null,
      projectedEndDate: '10/01/2024',
      dailyPumpingHours: null,
      avgTransitionTime: '--',
      completedStages: 0,
      totalStages: 246,
      wells: [
        { name: 'TEX-HARVEY SPRABERRY #2081', completed: 0, proposed: 86 },
        { name: 'TEX-HARVEY SPRABERRY #172', completed: 0, proposed: 78 },
        { name: 'TEX-HARVEY SPRABERRY #271', completed: 0, proposed: 82 },
      ],
    },
  ];

  // Filter and sort jobs for dashboard
  const filteredJobs = useMemo(() => {
    let filtered = jobsData.filter(job => {
      const matchesSearch = 
        job.jobName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.operator.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.fleet.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.district.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || job.status.toLowerCase() === statusFilter.toLowerCase();
      
      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = a.jobName;
          bVal = b.jobName;
          break;
        case 'operator':
          aVal = a.operator;
          bVal = b.operator;
          break;
        case 'efficiency':
          aVal = a.fracEfficiency || 0;
          bVal = b.fracEfficiency || 0;
          break;
        case 'progress':
          aVal = a.completedStages / a.totalStages;
          bVal = b.completedStages / b.totalStages;
          break;
        case 'status':
          const statusOrder = { 'Active': 0, 'Scheduled': 1, 'Completed': 2 };
          aVal = statusOrder[a.status];
          bVal = statusOrder[b.status];
          break;
        default:
          aVal = a.jobName;
          bVal = b.jobName;
      }
      
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [searchTerm, sortBy, sortOrder, statusFilter]);
  
  // Sample configuration data (from configuration_rswa.xlsx structure)
  const sampleConfig = {
    company: 'Endeavor Energy Resources',
    padName: 'WTH 20-8 Pad 4',
    wells: [
      { name: 'TEX-HARVEY SPRABERRY #2081', api: '4217338842', stages: 86, tvd: 7735, td: 21100, curveStart: 7381, curveEnd: 8173, status: 'Active' },
      { name: 'TEX-HARVEY SPRABERRY #172', api: '4217338801', stages: 78, tvd: 7680, td: 20450, curveStart: 7350, curveEnd: 8100, status: 'Planned' },
      { name: 'TEX-HARVEY SPRABERRY #271', api: '4217338802', stages: 82, tvd: 7720, td: 20800, curveStart: 7370, curveEnd: 8150, status: 'Planned' },
    ],
    state: 'Texas',
    county: 'Glasscock',
    formation: 'LSSS JoMill',
    fleet: 1,
    afe: 'ODR210331',
    casing: { od: 5.5, id: 4.778, weight: 20, rating: 'P-110' },
  };

  // Key components for landing page
  const keyComponents = [
    { 
      id: 'design', 
      title: 'Plan', 
      priority: 1,
      icon: '🎨',
      desc: 'New job workflow & configuration',
      color: '#3E76FE',
      features: ['Pad setup', 'Well configuration', 'Fleet scheduling', 'Job deployment']
    },
    { 
      id: 'completion-design', 
      title: 'Design', 
      priority: 2,
      icon: '🔧',
      desc: 'Pump schedule creator & editor',
      color: '#8CC540',
      features: ['Stage-by-stage design', 'Perforation parameters', 'Treatment planning', 'Export to operations']
    },
    // { 
    //   id: 'dashboard', 
    //   title: 'Analyze', 
    //   priority: 3,
    //   icon: '📊',
    //   desc: 'Real-time monitoring & insights',
    //   color: '#F9C844',
    //   features: ['Fuel analysis prediction', '3D Pad layouts', 'Well technicals', 'Performance metrics']
    // },
    { 
      id: 'machina-clf', 
      title: 'Closed Loop Frac', 
      priority: 4,
      icon: '🤖',
      desc: 'Intervention capabilities',
      color: '#BD9EFF',
      features: ['Supervised operations', 'AI-assisted decisions', 'Automated workflows', 'Real-time adjustments']
    },
    { 
      id: 'pad-camera', 
      title: 'Pad Camera View', 
      priority: 5,
      icon: '📹',
      desc: 'Live monitoring & surveillance',
      color: '#00BCD4',
      features: ['Live video feeds', 'Multi-camera toggle', 'Recording playback', 'Incident detection']
    },
  ];

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme === 'dark' ? '#1A1D1F' : '#F5F7FA',
      color: theme === 'dark' ? '#E5E7EB' : '#1A1D1F',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 32px',
      borderBottom: `1px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
      backgroundColor: theme === 'dark' ? '#232629' : '#FFFFFF',
    },
    logo: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '2px',
    },
    logoText: {
      fontSize: '28px',
      fontWeight: 900,
      letterSpacing: '-1px',
      color: theme === 'dark' ? '#FFFFFF' : '#1A1D1F',
    },
    logoAccent: {
      fontSize: '12px',
      color: '#F9C844',
      position: 'relative',
      top: '-12px',
    },
    nav: {
      display: 'flex',
      gap: '8px',
    },
    navBtn: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.2s',
    },
    content: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: theme === 'dark' ? '#232629' : '#FFFFFF',
      borderRadius: '12px',
      border: `1px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
      padding: '24px',
      marginBottom: '24px',
    },
    progressBar: {
      display: 'flex',
      justifyContent: 'space-between',
      position: 'relative',
      marginBottom: '40px',
    },
    progressLine: {
      position: 'absolute',
      top: '24px',
      left: '10%',
      right: '10%',
      height: '3px',
      backgroundColor: theme === 'dark' ? '#303335' : '#E0E0E0',
      zIndex: 0,
    },
    progressLineFill: {
      position: 'absolute',
      top: '24px',
      left: '10%',
      height: '3px',
      backgroundColor: '#8CC540',
      zIndex: 1,
      transition: 'width 0.3s ease',
    },
    stepItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 2,
      flex: 1,
    },
    stepCircle: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      marginBottom: '12px',
      transition: 'all 0.3s',
    },
    stepTitle: {
      fontSize: '14px',
      fontWeight: 600,
      marginBottom: '4px',
    },
    stepDesc: {
      fontSize: '12px',
      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
      textAlign: 'center',
      maxWidth: '140px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
    },
    componentCard: {
      backgroundColor: theme === 'dark' ? '#232629' : '#FFFFFF',
      borderRadius: '16px',
      border: `1px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
      padding: '24px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    priorityBadge: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 700,
      color: '#FFFFFF',
    },
    btn: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      transition: 'all 0.2s',
    },
    btnPrimary: {
      backgroundColor: '#8CC540',
      color: '#FFFFFF',
    },
    btnSecondary: {
      backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB',
      color: theme === 'dark' ? '#E5E7EB' : '#1A1D1F',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: `2px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
    },
    td: {
      padding: '16px',
      borderBottom: `1px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: theme === 'dark' ? '#232629' : '#FFFFFF',
      borderRadius: '16px',
      padding: '32px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
    },
    ganttRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
      height: '40px',
    },
    ganttLabel: {
      width: '120px',
      fontSize: '12px',
      fontWeight: 500,
      paddingRight: '16px',
    },
    ganttTrack: {
      flex: 1,
      height: '32px',
      backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB',
      borderRadius: '4px',
      position: 'relative',
      overflow: 'hidden',
    },
    ganttBar: {
      position: 'absolute',
      height: '100%',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '8px',
      fontSize: '11px',
      fontWeight: 600,
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'opacity 0.2s',
    },
    wellCard: {
      backgroundColor: theme === 'dark' ? '#303335' : '#F5F7FA',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
      border: `2px solid transparent`,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    stageBar: {
      display: 'flex',
      gap: '2px',
      marginTop: '12px',
    },
    stage: {
      flex: 1,
      height: '24px',
      borderRadius: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '9px',
      fontWeight: 600,
      color: 'rgba(255,255,255,0.8)',
    },
  };

  // Landing Page
  const LandingPage = () => (
    <div style={styles.content}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '16px', fontWeight: 700 }}>
          Machina
        </h1>
        <p style={{ fontSize: '18px', color: theme === 'dark' ? '#9CA3AF' : '#6B7280', maxWidth: '600px', margin: '0 auto' }}>
          Integrated completion operations management system. Select a component below or start a new job workflow.
        </p>
      </div>

      {/* <div style={{ ...styles.card, background: `linear-gradient(135deg, ${theme === 'dark' ? '#232629' : '#FFFFFF'} 0%, ${theme === 'dark' ? '#1A1D1F' : '#F5F7FA'} 100%)` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Quick Actions</h2>
          <button 
            style={{ ...styles.btn, ...styles.btnPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => navigate('/plan?action=new')}
          >
            <span>➕</span> New Job Workflow
          </button>
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowUploadModal(true)}>
            📤 Upload Configuration
          </button>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setCurrentScreen('calendar')}>
            📅 View Calendar
          </button>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setCurrentScreen('dashboard')}>
            📊 Open Dashboard
          </button>
        </div>
      </div> */}

      <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>
        Key Components <span style={{ fontSize: '14px', color: '#9CA3AF', fontWeight: 400 }}>(Priority Order)</span>
      </h2>
      
      <div style={styles.grid}>
        {keyComponents.map((comp) => (
          <div 
            key={comp.id}
            style={{
              ...styles.componentCard,
              borderLeftWidth: '4px',
              borderLeftColor: comp.color,
            }}
            onClick={() => {
              if (comp.id === 'design') { navigate('/plan'); }
              else if (comp.id === 'completion-design') navigate('/completions');
              else if (comp.id === 'dashboard') setCurrentScreen('dashboard');
              else if (comp.id === 'pad-camera') setCurrentScreen('camera');
              else if (comp.id === 'machina-clf') navigate('/pad-data');
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 12px 40px ${comp.color}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ ...styles.priorityBadge, backgroundColor: comp.color }}>
              {comp.priority}
            </div>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>{comp.icon}</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{comp.title}</h3>
            <p style={{ fontSize: '14px', color: theme === 'dark' ? '#9CA3AF' : '#6B7280', marginBottom: '16px' }}>
              {comp.desc}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {comp.features.map((f, i) => (
                <span key={i} style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: `${comp.color}20`,
                  color: comp.color,
                  fontWeight: 500,
                }}>
                  {f}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const startWorkflow = () => {
    resetWorkflow();
    setCurrentScreen('workflow');
    setCurrentStep(0);
  };

  // Workflow Screen
  const WorkflowScreen = () => {
    const progressWidth = (currentStep / (WORKFLOW_STEPS.length - 1)) * 80;
    const isLastStep = currentStep === WORKFLOW_STEPS.length - 1;
    const canProceed = validateStep(currentStep);

    const handleBack = () => {
      if (currentStep > 0) {
        goToPreviousStep();
      } else {
        resetWorkflow();
        setCurrentScreen('landing');
      }
    };

    const handleNext = () => {
      if (canProceed) {
        goToNextStep();
      }
    };

    return (
      <div style={styles.content}>
        {/* Progress Bar */}
        <div style={styles.progressBar}>
          <div style={styles.progressLine} />
          <div style={{ ...styles.progressLineFill, width: `${progressWidth}%` }} />
          {WORKFLOW_STEPS.map((step, i) => (
            <div key={step.id} style={styles.stepItem}>
              <div style={{
                ...styles.stepCircle,
                backgroundColor: i <= currentStep ? '#8CC540' : (theme === 'dark' ? '#303335' : '#E5E7EB'),
                color: i <= currentStep ? '#FFFFFF' : (theme === 'dark' ? '#6B7280' : '#9CA3AF'),
                border: i === currentStep ? '3px solid #F9C844' : 'none',
              }}>
                {i < currentStep ? '✓' : step.icon}
              </div>
              <div style={{ ...styles.stepTitle, color: i <= currentStep ? (theme === 'dark' ? '#FFFFFF' : '#1A1D1F') : '#6B7280' }}>
                {step.title}
              </div>
              <div style={styles.stepDesc}>{step.desc}</div>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div style={styles.card}>
          {currentStep === 0 && <Pads />}
          {currentStep === 1 && <WellConfig />}
          {currentStep === 2 && <FracSchedule />}
          {currentStep === 3 && (
            <PadDeploy onEdit={(step) => setCurrentStep(step)} />
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <button 
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={handleBack}
          >
            ← {currentStep > 0 ? 'Previous' : 'Back to Home'}
          </button>
          {isLastStep ? (
            <button 
              style={{ ...styles.btn, ...styles.btnPrimary, opacity: canProceed ? 1 : 0.6 }}
              onClick={handleNext}
              disabled={!canProceed}
            >
              🚀 Deploy Job
            </button>
          ) : (
            <button 
              style={{ ...styles.btn, ...styles.btnPrimary, opacity: canProceed ? 1 : 0.6 }}
              onClick={handleNext}
              disabled={!canProceed}
            >
              Next Step →
            </button>
          )}
        </div>
      </div>
    );
  };

  // Frac Calendar Screen
  const FracCalendarScreen = () => {
    const districts = [
      { name: 'Northeast', color: '#3E76FE' },
      { name: 'Vernal', color: '#8CC540' },
      { name: 'Longview', color: '#00BCD4' },
      { name: 'Pleasanton', color: '#F9C844' },
      { name: 'Midland', color: '#BD9EFF' },
    ];

    const fleets = [
      { name: '1000|18', district: 'Northeast', jobs: [{ start: 5, width: 30, operator: 'EOG Resources', color: '#3E76FE' }] },
      { name: '1000|20', district: 'Northeast', jobs: [{ start: 10, width: 25, operator: 'Devon Energy', color: '#3E76FE' }] },
      { name: '2100|37', district: 'Midland', jobs: [{ start: 0, width: 40, operator: 'ConocoPhillips', color: '#BD9EFF' }] },
      { name: '2100|38', district: 'Midland', jobs: [{ start: 45, width: 30, operator: 'Magnolia Oil', color: '#BD9EFF' }] },
      { name: '4000|09', district: 'Pleasanton', jobs: [{ start: 20, width: 35, operator: 'Apache Corp', color: '#F9C844' }] },
      { name: '4000|12', district: 'Vernal', jobs: [] },
      { name: '5000|04', district: 'Longview', jobs: [{ start: 15, width: 45, operator: 'SM Energy', color: '#00BCD4' }] },
      { name: '5000|08', district: 'Longview', jobs: [{ start: 0, width: 20, operator: 'Crescent Energy', color: '#00BCD4' }] },
    ];

    return (
      <div style={styles.content}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>Frac Calendar</h1>
            <p style={{ color: '#9CA3AF', margin: 0 }}>Fleet scheduling and job management</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button style={{ ...styles.btn, ...styles.btnSecondary }}>
              📅 Today
            </button>
            <button style={{ ...styles.btn, ...styles.btnPrimary }}>
              ➕ Add Job
            </button>
          </div>
        </div>

        {/* District Filter */}
        <div style={{ ...styles.card, padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#9CA3AF' }}>DISTRICTS:</span>
          {districts.map((d) => (
            <button
              key={d.name}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: `2px solid ${d.color}`,
                backgroundColor: `${d.color}20`,
                color: d.color,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {d.name}
            </button>
          ))}
        </div>

        {/* Calendar */}
        <div style={styles.card}>
          {/* Date Headers */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${theme === 'dark' ? '#303335' : '#E0E0E0'}`, paddingBottom: '12px', marginBottom: '12px' }}>
            <div style={{ width: '140px' }} />
            {['Jan 20', 'Jan 27', 'Feb 3', 'Feb 10', 'Feb 17', 'Feb 24', 'Mar 3', 'Mar 10'].map((d, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '12px', color: '#9CA3AF' }}>{d}</div>
            ))}
          </div>

          {/* Fleet Rows */}
          {fleets.map((fleet, i) => {
            const districtData = districts.find(d => d.name === fleet.district);
            return (
              <div key={i} style={{ ...styles.ganttRow, height: '48px' }}>
                <div style={{ ...styles.ganttLabel, width: '140px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '4px', height: '32px', borderRadius: '2px', backgroundColor: districtData?.color }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{fleet.name}</div>
                    <div style={{ fontSize: '10px', color: districtData?.color }}>{fleet.district}</div>
                  </div>
                </div>
                <div style={{ ...styles.ganttTrack, height: '36px' }}>
                  {fleet.jobs.map((job, j) => (
                    <div 
                      key={j}
                      style={{
                        ...styles.ganttBar,
                        left: `${job.start}%`,
                        width: `${job.width}%`,
                        backgroundColor: job.color,
                        borderRadius: '6px',
                      }}
                    >
                      {job.operator}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Dashboard Screen
  // Dashboard Screen - Operations Dashboard with Accordion
  const DashboardScreen = () => {
    const cardBg = theme === 'dark' ? '#232629' : '#FFFFFF';
    const border = theme === 'dark' ? '#303335' : '#E0E0E0';
    const text = theme === 'dark' ? '#E5E7EB' : '#1A1D1F';
    const muted = theme === 'dark' ? '#9CA3AF' : '#6B7280';
    const bg = theme === 'dark' ? '#1A1D1F' : '#F5F7FA';

    // Status badge component
    const StatusBadge = ({ status }) => {
      const colors = {
        'Active': { bg: '#8CC54020', color: '#8CC540', icon: '●' },
        'Completed': { bg: '#3E76FE20', color: '#3E76FE', icon: '✓' },
        'Scheduled': { bg: '#F9C84420', color: '#F9C844', icon: '◷' },
      };
      const c = colors[status] || colors['Scheduled'];
      return (
        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, backgroundColor: c.bg, color: c.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span>{c.icon}</span> {status}
        </span>
      );
    };

    // Mini gauge for accordion
    const MiniGauge = ({ value }) => {
      if (value === null) return <span style={{ color: muted }}>--</span>;
      const color = value >= 98 ? '#8CC540' : value >= 95 ? '#F9C844' : '#DC3545';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '40px', height: '6px', backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${value}%`, height: '100%', backgroundColor: color, borderRadius: '3px' }} />
          </div>
          <span style={{ fontSize: '13px', fontWeight: 600, color }}>{value}%</span>
        </div>
      );
    };

    // Progress bar component
    const ProgressBar = ({ completed, total }) => {
      const pct = total > 0 ? (completed / total) * 100 : 0;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1, height: '8px', backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct === 100 ? '#3E76FE' : '#8CC540', borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '12px', color: text, minWidth: '70px' }}>{completed}/{total}</span>
        </div>
      );
    };

    // Pressure/Rate chart component
    const PressureRateChart = () => {
      const pressureData = [7800, 7600, 7400, 7200, 7000, 7400, 7200, 7100, 7300, 7336, 7400, 7250, 7100, 7350, 7280];
      const rateData = [102, 105, 108, 104, 95, 102, 108, 106, 105, 108, 103, 107, 104, 106, 108];
      
      return (
        <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>Average Pressure and Average Rate</div>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: text }}>7,336</div>
                <div style={{ fontSize: '9px', color: muted }}>Well Avg Pressure (psi)</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: text }}>108</div>
                <div style={{ fontSize: '9px', color: muted }}>Well Avg Rate (bpm)</div>
              </div>
            </div>
          </div>
          <div style={{ position: 'relative', height: '120px' }}>
            <svg viewBox="0 0 300 80" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
              {[0, 20, 40, 60, 80].map((y, i) => (
                <line key={i} x1="0" y1={y} x2="300" y2={y} stroke={theme === 'dark' ? '#303335' : '#E5E7EB'} strokeWidth="0.5" />
              ))}
              <polyline
                fill="none"
                stroke="#DC3545"
                strokeWidth="2"
                points={pressureData.map((v, i) => `${i * 21},${80 - ((v - 6800) / 1200) * 80}`).join(' ')}
              />
              <polyline
                fill="none"
                stroke="#DC3545"
                strokeWidth="1.5"
                strokeDasharray="4,2"
                points={rateData.map((v, i) => `${i * 21},${80 - ((v - 90) / 25) * 80}`).join(' ')}
              />
            </svg>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '8px', fontSize: '10px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '16px', height: '2px', backgroundColor: '#DC3545' }}></span>
              <span style={{ color: muted }}>Average Pressure</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '16px', height: '0', borderTop: '2px dashed #DC3545' }}></span>
              <span style={{ color: muted }}>Average Rate</span>
            </span>
          </div>
        </div>
      );
    };

    // Transition Time scatter plot component
    const TransitionTimeChart = ({ job }) => {
      const scatterData = [
        { well: '#111', color: '#DC3545', points: [{x: 15, y: 12}, {x: 22, y: 8}, {x: 35, y: 14}, {x: 48, y: 10}, {x: 62, y: 16}, {x: 78, y: 9}] },
        { well: '#112', color: '#DC3545', points: [{x: 18, y: 18}, {x: 28, y: 11}, {x: 42, y: 15}, {x: 55, y: 8}, {x: 68, y: 12}, {x: 85, y: 14}] },
        { well: '#113', color: '#000000', points: [{x: 12, y: 22}, {x: 25, y: 9}, {x: 38, y: 13}, {x: 52, y: 18}, {x: 65, y: 7}, {x: 80, y: 11}] },
        { well: '#121', color: '#000000', points: [{x: 20, y: 15}, {x: 32, y: 20}, {x: 45, y: 11}, {x: 58, y: 14}, {x: 72, y: 17}, {x: 88, y: 10}] },
        { well: '#123', color: '#DC3545', points: [{x: 14, y: 9}, {x: 30, y: 16}, {x: 40, y: 12}, {x: 50, y: 19}, {x: 70, y: 8}, {x: 82, y: 13}] },
        { well: '#224', color: '#000000', points: [{x: 17, y: 14}, {x: 26, y: 21}, {x: 44, y: 10}, {x: 56, y: 15}, {x: 75, y: 11}, {x: 90, y: 16}] },
      ];

      return (
        <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: text, marginBottom: '12px' }}>Transition Time</div>
          <div style={{ position: 'relative', height: '120px', marginLeft: '24px' }}>
            <svg viewBox="0 0 200 80" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
              {[0, 20, 40, 60, 80].map((y, i) => (
                <line key={i} x1="0" y1={y} x2="200" y2={y} stroke={theme === 'dark' ? '#303335' : '#E5E7EB'} strokeWidth="0.5" />
              ))}
              {scatterData.map((well, wi) => 
                well.points.map((p, pi) => (
                  <circle 
                    key={`${wi}-${pi}`} 
                    cx={(p.x / 100) * 200} 
                    cy={80 - ((p.y / 30) * 80)} 
                    r="4" 
                    fill={theme === 'dark' ? well.color : (well.color === '#000000' ? '#333' : well.color)} 
                    opacity="0.7"
                  />
                ))
              )}
            </svg>
            <div style={{ position: 'absolute', left: '-24px', top: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '8px', color: muted }}>
              <span>30</span>
              <span>20</span>
              <span>10</span>
              <span>0</span>
            </div>
            <div style={{ position: 'absolute', bottom: '-16px', left: 0, width: '100%', display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: muted }}>
              <span>0</span>
              <span>20</span>
              <span>40</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', fontSize: '9px', flexWrap: 'wrap' }}>
            {job.wells.slice(0, 6).map((well, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: i % 2 === 0 ? '#DC3545' : (theme === 'dark' ? '#FFF' : '#333') }}></span>
                <span style={{ color: muted }}>{well.name.split(' ').pop()}</span>
              </span>
            ))}
          </div>
        </div>
      );
    };

    // Detailed job view component
    const JobDetailView = ({ job }) => {
      const fracNPTBreakdown = [
        { category: 'Blender', minutes: 31 },
        { category: 'Chem Add', minutes: 16 },
        { category: 'Data Van', minutes: 64 },
        { category: 'Iron', minutes: 25 },
        { category: 'Misc Other', minutes: 12 },
        { category: 'Pump Maint.', minutes: 14 },
      ];

      const dailyData = [
        { date: '06-10', stages: 13, pumpHours: '18 hrs, 48 mins', npt: 8 },
        { date: '06-11', stages: 14, pumpHours: '19 hrs, 12 mins', npt: 18 },
        { date: '06-12', stages: 17, pumpHours: '22 hrs, 22 mins', npt: 14 },
        { date: '06-13', stages: 18, pumpHours: '20 hrs, 18 mins', npt: 0 },
        { date: '06-14', stages: 18, pumpHours: '21 hrs, 45 mins', npt: 0 },
        { date: '06-15', stages: 18, pumpHours: '22 hrs, 6 mins', npt: 0 },
      ];

      if (job.status === 'Scheduled') {
        return (
          <div style={{ padding: '20px', backgroundColor: bg, borderTop: `1px solid ${border}` }}>
            <div style={{ textAlign: 'center', padding: '40px', color: muted }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <div style={{ fontSize: '16px', fontWeight: 500 }}>Job Not Yet Started</div>
              <div style={{ fontSize: '13px', marginTop: '8px' }}>Operational data will be available once the job begins.</div>
              <div style={{ marginTop: '16px' }}>
                <span style={{ fontSize: '13px' }}>Scheduled Start: </span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#F9C844' }}>{job.projectedEndDate}</span>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div style={{ padding: '20px', backgroundColor: bg, borderTop: `1px solid ${border}` }}>
          {/* Stats Grid */}
          <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '16px' }}>
              {[
                { label: 'Total Pump Time', value: job.totalPumpTime, color: '#DC3545' },
                { label: 'Total NPT', value: job.totalNPT, color: '#DC3545' },
                { label: 'Avg Sand/Stage', value: '239,778 lbs', color: '#DC3545' },
                { label: 'Weather NPT', value: '9 hrs, 7 mins', color: '#DC3545' },
                { label: 'Avg Pump Time', value: '72 mins', color: '#8CC540' },
                { label: 'Avg Daily Stages', value: job.avgDailyStageCount?.toFixed(1) || '--', color: '#8CC540' },
                { label: 'Avg Transition', value: job.avgTransitionTime, color: '#8CC540' },
                { label: 'Daily Pump Hrs', value: job.dailyPumpingHours?.toFixed(1) || '--', color: '#8CC540' },
              ].map((stat, i) => (
                <div key={i}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '10px', color: muted }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Row - Pressure/Rate and Transition Time */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <PressureRateChart />
            <TransitionTimeChart job={job} />
          </div>

          {/* Bottom Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 280px', gap: '12px' }}>
            {/* Daily Stage Counts */}
            <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: text, marginBottom: '10px' }}>Daily Stage Counts</div>
              {dailyData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '9px', color: muted, width: '32px' }}>{d.date}</span>
                  <div style={{ flex: 1, height: '12px', backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB', borderRadius: '2px' }}>
                    <div style={{ width: `${(d.stages / 20) * 100}%`, height: '100%', backgroundColor: '#DC3545', borderRadius: '2px' }} />
                  </div>
                  <span style={{ fontSize: '9px', color: text, width: '16px' }}>{d.stages}</span>
                </div>
              ))}
            </div>

            {/* Daily Pump Hours */}
            <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: text, marginBottom: '10px' }}>Daily Pump Hours</div>
              {dailyData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '9px', color: muted, width: '32px' }}>{d.date}</span>
                  <div style={{ flex: 1, height: '12px', backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: `${(parseInt(d.pumpHours) / 24) * 100}%`, height: '100%', backgroundColor: '#DC3545', borderRadius: '2px' }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Daily Frac NPT */}
            <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: text, marginBottom: '10px' }}>Daily Frac NPT</div>
              {dailyData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '9px', color: muted, width: '32px' }}>{d.date}</span>
                  <div style={{ flex: 1, height: '12px', backgroundColor: theme === 'dark' ? '#303335' : '#E5E7EB', borderRadius: '2px' }}>
                    {d.npt > 0 && <div style={{ width: `${Math.min((d.npt / 60) * 100, 100)}%`, height: '100%', backgroundColor: '#DC3545', borderRadius: '2px' }} />}
                  </div>
                  <span style={{ fontSize: '9px', color: d.npt > 0 ? '#DC3545' : muted, width: '28px' }}>{d.npt}m</span>
                </div>
              ))}
            </div>

            {/* Wells & NPT */}
            <div>
              <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: text, marginBottom: '10px' }}>Wells Progress</div>
                {job.wells.map((w, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '11px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: w.completed === w.proposed ? '#3E76FE' : '#8CC540' }} />
                    <span style={{ flex: 1, color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name.split(' ').slice(-1)[0]}</span>
                    <span style={{ color: muted }}>{w.completed}/{w.proposed}</span>
                  </div>
                ))}
              </div>

              <div style={{ backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${border}`, padding: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: text, marginBottom: '10px' }}>Frac NPT Breakdown</div>
                {fracNPTBreakdown.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                    <span style={{ color: text }}>{item.category}</span>
                    <span style={{ color: '#DC3545', fontWeight: 600 }}>{item.minutes}m</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, borderTop: `1px solid ${border}`, paddingTop: '6px', marginTop: '6px' }}>
                  <span style={{ color: text }}>Total</span>
                  <span style={{ color: '#DC3545' }}>{fracNPTBreakdown.reduce((a, b) => a + b.minutes, 0)}m</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div style={styles.content}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>📊 Operations Dashboard</h1>
            <p style={{ color: '#9CA3AF', margin: 0 }}>Monitor job performance and operational metrics</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: muted }}>{filteredJobs.length} jobs</span>
          </div>
        </div>

        {/* Search and Filters */}
        <div style={{ ...styles.card, padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <div style={{ flex: 1, minWidth: '250px' }}>
              <input
                type="text"
                placeholder="🔍 Search by job name, operator, fleet, or district..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  backgroundColor: bg,
                  color: text,
                  fontSize: '14px',
                }}
              />
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {['all', 'active', 'scheduled', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: statusFilter === status ? '#8CC540' : (theme === 'dark' ? '#303335' : '#E5E7EB'),
                    color: statusFilter === status ? '#FFF' : text,
                    textTransform: 'capitalize',
                  }}
                >
                  {status === 'all' ? 'All Jobs' : status}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: muted }}>Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${border}`,
                  backgroundColor: bg,
                  color: text,
                  fontSize: '12px',
                }}
              >
                <option value="name">Job Name</option>
                <option value="operator">Operator</option>
                <option value="efficiency">Frac Efficiency</option>
                <option value="progress">Progress</option>
                <option value="status">Status</option>
              </select>
              <button
                onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${border}`,
                  backgroundColor: bg,
                  color: text,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Column Headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 0.8fr 0.6fr 0.8fr 0.8fr 1.2fr 40px', gap: '12px', padding: '12px 16px', fontSize: '11px', fontWeight: 600, color: muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <div>Job Name</div>
          <div>Operator</div>
          <div>Status</div>
          <div>Efficiency</div>
          <div>Pump Time</div>
          <div>NPT</div>
          <div>Stages/Day</div>
          <div>Trans. Time</div>
          <div>Progress</div>
          <div></div>
        </div>

        {/* Accordion Jobs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredJobs.map(job => (
            <div key={job.id} style={{ backgroundColor: cardBg, borderRadius: '10px', border: `1px solid ${expandedJob === job.id ? '#8CC540' : border}`, overflow: 'hidden', transition: 'border-color 0.2s' }}>
              {/* Accordion Header */}
              <div
                onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 0.8fr 0.6fr 0.8fr 0.8fr 1.2fr 40px',
                  gap: '12px',
                  padding: '16px',
                  cursor: 'pointer',
                  alignItems: 'center',
                }}
              >
                {/* Job Name & Fleet */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: text, marginBottom: '2px' }}>{job.jobName}</div>
                  <div style={{ fontSize: '11px', color: muted }}>{job.fleet} • {job.district}</div>
                </div>

                {/* Operator */}
                <div style={{ fontSize: '13px', color: text }}>{job.operator}</div>

                {/* Status */}
                <div><StatusBadge status={job.status} /></div>

                {/* Frac Efficiency */}
                <div><MiniGauge value={job.fracEfficiency} /></div>

                {/* Pump Time */}
                <div style={{ fontSize: '12px', color: job.totalPumpTime === '--' ? muted : text }}>{job.totalPumpTime === '--' ? '--' : job.totalPumpTime.split(',')[0]}</div>

                {/* NPT */}
                <div style={{ fontSize: '12px', color: job.totalNPT === '--' ? muted : '#DC3545' }}>{job.totalNPT === '--' ? '--' : job.totalNPT.split(',')[0]}</div>

                {/* Avg Daily Stages */}
                <div style={{ fontSize: '13px', fontWeight: 500, color: job.avgDailyStageCount ? text : muted }}>{job.avgDailyStageCount?.toFixed(1) || '--'}</div>

                {/* Avg Transition */}
                <div style={{ fontSize: '12px', color: job.avgTransitionTime === '--' ? muted : text }}>{job.avgTransitionTime}</div>

                {/* Progress */}
                <div><ProgressBar completed={job.completedStages} total={job.totalStages} /></div>

                {/* Expand Icon */}
                <div style={{ fontSize: '16px', color: muted, transition: 'transform 0.2s', transform: expandedJob === job.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </div>
              </div>

              {/* Expanded Detail View */}
              {expandedJob === job.id && <JobDetailView job={job} />}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredJobs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: muted }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', fontWeight: 500 }}>No jobs found</div>
            <div style={{ fontSize: '13px', marginTop: '8px' }}>Try adjusting your search or filters</div>
          </div>
        )}
      </div>
    );
  };

  // Completion Design Screen
  const CompletionScreen = () => (
    <div style={styles.content}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>Completion Design Tool</h1>
          <p style={{ color: '#9CA3AF', margin: 0 }}>Pump schedule creator & editor</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }}>
            📋 Copy Stage Design
          </button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }}>
            💾 Save Changes
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
        {/* Wells Sidebar */}
        <div style={styles.card}>
          <h3 style={{ fontSize: '14px', marginBottom: '16px', color: '#9CA3AF' }}>SELECT WELL</h3>
          {sampleConfig.wells.map((well, i) => (
            <div 
              key={i}
              style={{
                ...styles.wellCard,
                marginBottom: '12px',
                borderColor: selectedWell === i ? '#8CC540' : 'transparent',
              }}
              onClick={() => setSelectedWell(i)}
            >
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>{well.name}</div>
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{well.stages} stages</div>
            </div>
          ))}
        </div>

        {/* Stage Editor */}
        <div style={styles.card}>
          <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>Stage Configuration</h3>
          
          {/* Stage Bar */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: '#9CA3AF' }}>TOE</span>
              <span style={{ fontSize: '11px', color: '#9CA3AF' }}>HEEL</span>
            </div>
            <div style={{ display: 'flex', gap: '3px' }}>
              {Array.from({ length: 20 }).map((_, j) => (
                <div 
                  key={j}
                  style={{
                    flex: 1,
                    height: '36px',
                    backgroundColor: j < 8 ? '#8CC540' : j === 8 ? '#F9C844' : '#303335',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    border: j === 5 ? '2px solid #FFFFFF' : 'none',
                  }}
                >
                  {j + 1}
                </div>
              ))}
            </div>
          </div>

          {/* Stage Details */}
          <div style={{ 
            backgroundColor: theme === 'dark' ? '#1A1D1F' : '#F5F7FA', 
            borderRadius: '12px', 
            padding: '20px' 
          }}>
            <h4 style={{ fontSize: '14px', marginBottom: '16px' }}>Stage 6 Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              {[
                { label: 'Top Perf', value: '20087 ft' },
                { label: 'Bottom Perf', value: '20207 ft' },
                { label: '# of Perfs', value: '36' },
                { label: 'Perf Size', value: '0.42"' },
                { label: 'Clusters', value: '6' },
                { label: 'Shots/Cluster', value: '6' },
                { label: 'Cluster Spacing', value: '20 ft' },
                { label: 'Uniformity Index', value: '0.85' },
                { label: 'Lbs/Foot', value: '2,500' },
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Scheduled Job Screen (after workflow completion)
  const ScheduledJobScreen = () => (
    <div style={styles.content}>
      <div style={{ 
        textAlign: 'center', 
        padding: '48px',
        backgroundColor: '#8CC54015',
        borderRadius: '16px',
        border: '2px solid #8CC540',
        marginBottom: '32px',
      }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
        <h1 style={{ fontSize: '32px', color: '#8CC540', marginBottom: '8px' }}>Job Successfully Deployed!</h1>
        <p style={{ color: '#9CA3AF', fontSize: '16px' }}>
          {sampleConfig.padName} has been scheduled and is now visible in the Frac Calendar
        </p>
      </div>

      <div style={{ ...styles.card }}>
        <h2 style={{ fontSize: '20px', marginBottom: '24px' }}>Job Details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Pad</div>
            <div style={{ fontWeight: 600, fontSize: '18px' }}>{sampleConfig.padName}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Wells</div>
            <div style={{ fontWeight: 600, fontSize: '18px' }}>{sampleConfig.wells.length}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Total Stages</div>
            <div style={{ fontWeight: 600, fontSize: '18px' }}>{sampleConfig.wells.reduce((a, w) => a + w.stages, 0)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Scheduled Start</div>
            <div style={{ fontWeight: 600, fontSize: '18px' }}>Feb 10, 2026</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
        <button 
          style={{ ...styles.btn, ...styles.btnPrimary }}
          onClick={() => setCurrentScreen('calendar')}
        >
          📅 View in Calendar
        </button>
        <button 
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={() => setCurrentScreen('completion')}
        >
          🔧 Open Completion Design
        </button>
        <button 
          style={{ ...styles.btn, ...styles.btnSecondary }}
          onClick={() => setCurrentScreen('landing')}
        >
          🏠 Back to Home
        </button>
      </div>
    </div>
  );

  // Upload Modal
  const UploadModal = () => (
    <div style={styles.modal} onClick={() => setShowUploadModal(false)}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: '24px' }}>Upload Configuration</h2>
        
        <div style={{ 
          border: `2px dashed ${theme === 'dark' ? '#303335' : '#E0E0E0'}`,
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center',
          marginBottom: '24px',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📤</div>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>Drop your configuration_rswa.xlsx here</div>
          <div style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '16px' }}>or click to browse</div>
          <button style={{ ...styles.btn, ...styles.btnSecondary }}>Browse Files</button>
        </div>

        <div style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '24px' }}>
          <strong>Accepted format:</strong> .xlsx files following the configuration_rswa template structure
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowUploadModal(false)}>
            Cancel
          </button>
          <button 
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={() => {
              resetWorkflow();
              setShowUploadModal(false);
              setCurrentScreen('workflow');
              setCurrentStep(1);
            }}
          >
            Upload & Continue
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <Header title="Machina" />

      {/* Main Content */}
      {currentScreen === 'landing' && <LandingPage />}
      {currentScreen === 'workflow' && <WorkflowScreen />}
      {currentScreen === 'calendar' && <FracCalendarScreen />}
      {currentScreen === 'dashboard' && <DashboardScreen />}
      {currentScreen === 'completion' && <CompletionScreen />}
      {currentScreen === 'scheduled' && <ScheduledJobScreen />}
      {currentScreen === 'camera' && (
        <div style={styles.content}>
          <h1 style={{ fontSize: '28px', marginBottom: '24px' }}>📹 Pad Camera View</h1>
          <div style={{ ...styles.card, height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>📹</div>
              <div style={{ fontSize: '18px' }}>Live Camera Feed Placeholder</div>
              <div style={{ fontSize: '14px' }}>Multi-camera toggle • Recording playback • Incident detection</div>
            </div>
          </div>
        </div>
      )}
      {currentScreen === 'machina' && (
        <div style={styles.content}>
          <h1 style={{ fontSize: '28px', marginBottom: '24px' }}>Closed Loop Frac</h1>
          <div style={{ ...styles.card, height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🤖</div>
              <div style={{ fontSize: '18px' }}>Supervised AI Operations Interface</div>
              <div style={{ fontSize: '14px' }}>AI-assisted decisions • Automated workflows • Real-time adjustments</div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && <UploadModal />}
    </div>
  );
};

const MachinaPrototype = () => {
  const [searchParams] = useSearchParams();
  const [currentScreen, setCurrentScreen] = useState('landing');

  useEffect(() => {
    const screenParam = searchParams.get('screen');
    if (screenParam === 'dashboard') {
      setCurrentScreen('dashboard');
    } else {
      setCurrentScreen('landing');
    }
  }, [searchParams]);

  const handleWorkflowComplete = () => {
    setCurrentScreen('scheduled');
  };

  return (
    <WorkflowProvider onComplete={handleWorkflowComplete}>
      <MachinaPrototypeContent
        currentScreen={currentScreen}
        setCurrentScreen={setCurrentScreen}
      />
    </WorkflowProvider>
  );
};

export default MachinaPrototype;